import { useEffect, useState } from 'react'
import { Search } from 'lucide-react'
import type { SearchResult } from '@shared/types'
import { api } from '../../lib/api'
import { displaySymbol } from '../../lib/symbol'
import { cn } from '../../lib/utils'

/**
 * Exchange-agnostic symbol picker. Type a ticker OR a company name → live
 * EODHD results across all exchanges → pick one (returns the exact
 * CODE.EXCHANGE, e.g. BRK-B.US, VOD.LSE). Free text still works on Enter.
 */
export function SymbolSearch({
  value,
  onSelect,
  big,
  placeholder,
}: {
  value: string
  onSelect: (symbol: string) => void
  big?: boolean
  placeholder?: string
}) {
  const [q, setQ] = useState(displaySymbol(value))
  const [results, setResults] = useState<SearchResult[] | null>(null)
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)

  useEffect(() => {
    setQ(displaySymbol(value))
  }, [value])

  useEffect(() => {
    if (!open) return
    const term = q.trim()
    if (term.length < 1) {
      setResults(null)
      return
    }
    let alive = true
    const t = setTimeout(() => {
      api
        .search(term)
        .then((r) => {
          if (!alive) return
          setResults(r.slice(0, 8))
          setActive(0)
        })
        .catch(() => alive && setResults([]))
    }, 220)
    return () => {
      alive = false
      clearTimeout(t)
    }
  }, [q, open])

  const pick = (r: SearchResult) => {
    onSelect(r.symbol)
    setQ(displaySymbol(r.symbol))
    setOpen(false)
    setResults(null)
  }
  const commitRaw = () => {
    const t = q.trim()
    if (t) onSelect(t.toUpperCase())
    setOpen(false)
  }

  return (
    <div className="relative">
      <div
        className={cn(
          'flex items-center gap-2 rounded-xl border border-[var(--line-soft)] bg-[var(--color-inset)] transition-colors focus-within:border-[var(--line)]',
          big ? 'px-3.5 py-3' : 'px-2.5 py-2',
        )}
      >
        <Search size={big ? 17 : 14} className="shrink-0 text-fg-subtle" />
        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault()
              setActive((a) => Math.min(a + 1, (results?.length ?? 1) - 1))
            } else if (e.key === 'ArrowUp') {
              e.preventDefault()
              setActive((a) => Math.max(a - 1, 0))
            } else if (e.key === 'Enter') {
              e.preventDefault()
              if (results && results[active]) pick(results[active])
              else commitRaw()
            } else if (e.key === 'Escape') {
              setOpen(false)
            }
          }}
          placeholder={placeholder ?? 'Search symbol or company'}
          className={cn(
            'min-w-0 flex-1 bg-transparent uppercase text-fg placeholder:normal-case placeholder:text-fg-faint focus:outline-none',
            big ? 'text-[19px] font-semibold tracking-wide' : 'text-[13px]',
          )}
        />
      </div>
      {open && results && results.length > 0 && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute left-0 right-0 top-full z-40 mt-1 max-h-72 overflow-y-auto rounded-xl border border-[var(--line)] bg-[var(--color-surface-3)] p-1 shadow-[var(--e-3)]">
            {results.map((r, i) => (
              <button
                key={`${r.symbol}-${i}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(r)}
                className={cn(
                  'flex w-full items-center justify-between gap-3 rounded-lg px-2.5 py-2 text-left',
                  i === active ? 'bg-[var(--color-surface-2)]' : '',
                )}
              >
                <span className="min-w-0">
                  <span className="block truncate text-[13px] font-medium text-fg">{r.name}</span>
                  <span className="block text-[11px] text-fg-subtle">
                    {r.type} · {r.exchange}
                    {r.country ? ` · ${r.country}` : ''}
                  </span>
                </span>
                <span className="num shrink-0 rounded bg-[var(--color-inset)] px-1.5 py-0.5 text-[11.5px] font-semibold text-fg">
                  {r.code}
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
